import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import { ko } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import fnc from '../../mymod/commFunction';
import SmsSendMobileLayout from './SmsSendMobileLayout';
import SmsSendHistory from './SmsSendHistory';
import SmsTemplateModal from './SmsTemplateModal';
import './SmsSend.scss';

const TAB = {
    SMS: 'sms',
    MMS: 'mms',
    HISTORY: 'history'
};

const SMS_LIMIT_BYTES = 90;
const MESSAGE_PREFIX = '[매쓰테일러] ';
const MMS_MAX_FILES = 5;

function formatDateTimeForApi(dateValue, timeValue) {
    if (!dateValue) return '';
    const d = new Date(dateValue);
    if (Number.isNaN(d.getTime())) return '';

    const [timeHh = '00', timeMm = '00'] = String(timeValue || '').split(':');
    d.setHours(parseInt(timeHh, 10) || 0);
    d.setMinutes(parseInt(timeMm, 10) || 0);
    d.setSeconds(0);

    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${y}-${m}-${day} ${hh}:${mm}:${ss}`;
}

function nowForTimeInput() {
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
}

function normalizePhone(value) {
    const raw = String(value || '').trim();
    if (!raw || raw.toUpperCase() === 'NULL') return '';
    return raw.replace(/[^0-9]/g, '');
}

function formatAmount(value) {
    const number = Number(String(value ?? '').replace(/,/g, ''));
    return Number.isFinite(number) ? number.toLocaleString('ko-KR') : '0';
}

function cleanMessageText(value) {
    return String(value || '').replace(/\[매쓰테일러\]\s*/g, '').trim();
}

function messagePreview(value, maxLength = 18) {
    const text = cleanMessageText(value).replace(/\s+/g, ' ').trim();
    if (text.length <= maxLength) return text;
    return `${text.slice(0, maxLength)}…`;
}

function formatMessageDate(value) {
    const dateText = String(value || '').slice(0, 10);
    const [year, month, day] = dateText.split('-').map(Number);
    if (!year || !month || !day) return dateText;

    const date = new Date(year, month - 1, day);
    if (Number.isNaN(date.getTime())) return dateText;
    return `${dateText} ${'일월화수목금토'[date.getDay()]}`;
}

function isTodayMessageDate(value) {
    const dateText = String(value || '').slice(0, 10);
    const now = new Date();
    const todayText = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    return dateText === todayText;
}

function resolveUserPhone(user) {
    return (
        normalizePhone(user?.hp)
        || normalizePhone(user?.mobile)
        || normalizePhone(user?.phone)
        || ''
    );
}

function prependMessagePrefix(message) {
    const text = String(message || '');
    if (!text) return '';
    if (text.startsWith(MESSAGE_PREFIX) || text.startsWith('[매쓰테일러]')) return text;
    return `${MESSAGE_PREFIX}${text}`;
}

function joinUrl(base, path) {
    const b = String(base || '').replace(/\/$/, '');
    const p = String(path || '').replace(/^\//, '');
    return b ? `${b}/${p}` : `/${p}`;
}

function sanitizeFileName(name) {
    return String(name || 'file')
        .replace(/[^a-zA-Z0-9._-]/g, '_')
        .replace(/_+/g, '_');
}

function compareUsersByName(a, b) {
    const aName = String(a?.stName || '');
    const bName = String(b?.stName || '');
    const byName = aName.localeCompare(bName, 'ko', { sensitivity: 'base', numeric: true });
    if (byName !== 0) return byName;

    const aId = String(a?.id || '');
    const bId = String(b?.id || '');
    return aId.localeCompare(bId, 'ko', { sensitivity: 'base', numeric: true });
}

function toS3PublicUrl(s3Key) {
    const encodedKey = String(s3Key || '')
        .split('/')
        .map((part) => encodeURIComponent(part))
        .join('/');
    return `https://mathautogen.s3.ap-northeast-2.amazonaws.com/${encodedKey}`;
}

function loadImageFromFile(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error('이미지 로딩 실패'));
            img.src = reader.result;
        };
        reader.onerror = () => reject(new Error('파일 읽기 실패'));
        reader.readAsDataURL(file);
    });
}

async function convertToJpegFile(file) {
    const image = await loadImageFromFile(file);
    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('이미지 변환 컨텍스트 생성 실패');
    }

    // PNG 투명 영역이 검정으로 변하는 것을 방지하기 위해 흰 배경을 깐다.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(image, 0, 0, width, height);

    const jpegBlob = await new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92);
    });

    if (!jpegBlob) {
        throw new Error('JPG 변환 실패');
    }

    const rawName = String(file?.name || 'mms_image').replace(/\.[^/.]+$/, '');
    const safeBaseName = sanitizeFileName(rawName);
    return new File([jpegBlob], `${safeBaseName}.jpg`, { type: 'image/jpeg' });
}

const SmsSend = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const requestedTab = searchParams.get('tab');
    const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined'
        ? window.matchMedia('(max-width: 768px)').matches
        : false));
    const [tab, setTab] = useState(() => {
        if (requestedTab === TAB.HISTORY) return TAB.HISTORY;
        if (requestedTab === TAB.MMS) return TAB.MMS;
        return TAB.SMS;
    });
    const [sending, setSending] = useState(false);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [users, setUsers] = useState([]);
    const [selectedKeys, setSelectedKeys] = useState(new Set());
    const [isRowDragging, setIsRowDragging] = useState(false);
    const [dragStartSelectedCount, setDragStartSelectedCount] = useState(0);
    const [keyword, setKeyword] = useState('');
    const [sortKey, setSortKey] = useState('stName');
    const [sortDir, setSortDir] = useState('asc');
    const [mmsFiles, setMmsFiles] = useState([]);
    const [uploadingImages, setUploadingImages] = useState(false);
    const [templateModalOpen, setTemplateModalOpen] = useState(false);
    const rowDragSelectionRef = useRef({ active: false, mode: 'add' });

    const [form, setForm] = useState({
        mode: 1,
        sendDate: new Date(),
        sendTime: nowForTimeInput(),
        toPhone: '',
        personName: '',
        studentNo: '',
        message: '',
        subject: '',
        fileCount: 0,
        filePath1: '',
        filePath2: '',
        filePath3: '',
        filePath4: '',
        filePath5: ''
    });

    const tabLabel = useMemo(() => (tab === TAB.SMS ? 'SMS' : 'MMS'), [tab]);
    const smsMessageBytes = useMemo(() => new TextEncoder().encode(form.message || '').length, [form.message]);
    const isSmsOverLimit = useMemo(
        () => tab === TAB.SMS && smsMessageBytes > SMS_LIMIT_BYTES,
        [tab, smsMessageBytes]
    );

    useEffect(() => {
        fetchUsers();
    }, []);

    useEffect(() => {
        if (requestedTab === TAB.HISTORY) setTab(TAB.HISTORY);
        else if (requestedTab === TAB.MMS) setTab(TAB.MMS);
        else if (requestedTab === TAB.SMS) setTab(TAB.SMS);
    }, [requestedTab]);

    useEffect(() => {
        const stopRowDragSelection = () => {
            rowDragSelectionRef.current.active = false;
            setIsRowDragging(false);
        };

        window.addEventListener('mouseup', stopRowDragSelection);
        return () => window.removeEventListener('mouseup', stopRowDragSelection);
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return undefined;
        }

        const mediaQuery = window.matchMedia('(max-width: 768px)');
        const updateLayoutMode = (event) => {
            setIsMobile(event.matches);
        };

        setIsMobile(mediaQuery.matches);

        if (typeof mediaQuery.addEventListener === 'function') {
            mediaQuery.addEventListener('change', updateLayoutMode);
            return () => mediaQuery.removeEventListener('change', updateLayoutMode);
        }

        mediaQuery.addListener(updateLayoutMode);
        return () => mediaQuery.removeListener(updateLayoutMode);
    }, []);

    useEffect(() => {
        const onWindowError = (event) => {
            const msg = event?.message || '';
            if (msg.includes('ResizeObserver loop completed with undelivered notifications')) {
                event.stopImmediatePropagation();
            }
        };

        const originalConsoleError = console.error;
        console.error = (...args) => {
            const joined = args.map((arg) => String(arg)).join(' ');
            if (joined.includes('ResizeObserver loop completed with undelivered notifications')) {
                return;
            }
            originalConsoleError(...args);
        };

        window.addEventListener('error', onWindowError);
        return () => {
            window.removeEventListener('error', onWindowError);
            console.error = originalConsoleError;
        };
    }, []);

    const filteredUsers = useMemo(() => {
        const key = (keyword || '').trim().toLowerCase();
        if (!key) return users;

        return users.filter((user) => {
            const id = String(user.id || '').toLowerCase();
            const name = String(user.stName || '').toLowerCase();
            const stCode = String(user.stCode || '').toLowerCase();
            const hp = String(user.hp || '').toLowerCase();
            const academyName = String(user.academyName || '').toLowerCase();
            return id.includes(key) || name.includes(key) || stCode.includes(key) || hp.includes(key) || academyName.includes(key);
        });
    }, [users, keyword]);

    const sortedFilteredUsers = useMemo(() => {
        const copied = [...filteredUsers];
        copied.sort((a, b) => {
            if (sortKey === 'stName') {
                const result = compareUsersByName(a, b);
                return sortDir === 'asc' ? result : result * -1;
            }

            if (sortKey === 'used_amount' || sortKey === 'total_charged' || sortKey === 'lastLoginDiff' || sortKey === 'loginCount') {
                const result = (Number(a[sortKey]) || 0) - (Number(b[sortKey]) || 0);
                if (result !== 0) return sortDir === 'asc' ? result : result * -1;
                return compareUsersByName(a, b);
            }

            const aValue = String(a[sortKey] || '');
            const bValue = String(b[sortKey] || '');
            const result = aValue.localeCompare(bValue, 'ko', { sensitivity: 'base', numeric: true });
            if (result !== 0) return sortDir === 'asc' ? result : result * -1;

            return compareUsersByName(a, b);
        });
        return copied;
    }, [filteredUsers, sortKey, sortDir]);

    const selectedCount = useMemo(() => selectedKeys.size, [selectedKeys]);
    const displayedSelectedCount = isRowDragging ? dragStartSelectedCount : selectedCount;

    function getRowKey(user) {
        return `${user.id}__${user.stCode}__${user.hp}`;
    }

    const selectedUsers = useMemo(() => {
        const selected = users.filter((user) => selectedKeys.has(getRowKey(user)));
        selected.sort(compareUsersByName);
        return selected;
    }, [users, selectedKeys]);

    const allVisibleSelected = useMemo(
        () => sortedFilteredUsers.length > 0 && sortedFilteredUsers.every((user) => selectedKeys.has(getRowKey(user))),
        [sortedFilteredUsers, selectedKeys]
    );

    function onChange(key, value) {
        setForm((prev) => ({ ...prev, [key]: value }));
    }

    function sendAxiosAsync(url, data) {
        return fetch(joinUrl(fnc.svrUrl, url), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data || {})
        })
            .then(async (response) => {
                let json = {};
                try {
                    json = await response.json();
                } catch (e) {
                    json = {};
                }

                if (!response.ok) {
                    return {
                        success: false,
                        message: json?.message || `HTTP ${response.status}`,
                        detail: json?.detail || ''
                    };
                }

                return json || {};
            })
            .catch((error) => ({ success: false, message: error.message || '요청 실패' }));
    }

    function fetchUsers() {
        setLoadingUsers(true);
        setSortKey('stName');
        setSortDir('asc');
        const latestMessagesPromise = sendAxiosAsync('/smsRoutes/latest-messages', {});

        fnc.sendAxios('/userRoutes/selectUsers', {}, async (res) => {
            const latestResponse = await latestMessagesPromise;
            const latestRows = latestResponse?.success && Array.isArray(latestResponse.rows)
                ? latestResponse.rows
                : [];
            const latestByPhone = new Map(latestRows.map((row) => [normalizePhone(row.phone), row]));
            const mapped = Array.isArray(res)
                ? res.map((item) => {
                    const phoneKey = resolveUserPhone(item);
                    const latestMessage = latestByPhone.get(phoneKey) || {};
                    return {
                        id: item.id || '',
                        stName: item.stName || '',
                        stCode: item.stCode || '',
                        hp: item.hp || '',
                        mobile: item.mobile || '',
                        phone: item.phone || '',
                        used_amount: Number(item.wallet_total_used) || 0,
                        total_charged: Number(item.wallet_total_charged) || 0,
                        lastLoginAt: item.last_login_at || '',
                        lastLoginDiff: item.last_login_diff !== null
                            && item.last_login_diff !== undefined
                            && Number.isFinite(Number(item.last_login_diff))
                            ? Number(item.last_login_diff)
                            : '',
                        loginCount: Number(item.login_count) || 0,
                        sDate: item.sDate || '',
                        academyName: item.academyName || '',
                        lastMessage: cleanMessageText(latestMessage.message),
                        lastMessageAt: latestMessage.sent_at || ''
                    };
                })
                : [];

            setUsers(mapped);
            setLoadingUsers(false);
        });
    }

    function toggleUser(user) {
        const key = getRowKey(user);
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }

    function handleUserRowMouseDown(event, user, index) {
        if (event.button !== 0) return;

        event.preventDefault();
        const key = getRowKey(user);
        const preserveSelection = event.ctrlKey || event.metaKey;
        const mode = preserveSelection && selectedKeys.has(key) ? 'remove' : 'add';
        const baseKeys = preserveSelection ? new Set(selectedKeys) : new Set();
        rowDragSelectionRef.current = { active: true, mode, startIndex: index, baseKeys };
        setDragStartSelectedCount(selectedKeys.size);
        setIsRowDragging(true);

        const nextKeys = new Set(baseKeys);
        if (mode === 'remove') nextKeys.delete(key);
        else nextKeys.add(key);
        setSelectedKeys(nextKeys);
    }

    function handleUserRowMouseEnter(event, index) {
        if (!rowDragSelectionRef.current.active) return;
        if ((event.buttons & 1) !== 1) {
            rowDragSelectionRef.current.active = false;
            setIsRowDragging(false);
            return;
        }

        const { mode, startIndex, baseKeys } = rowDragSelectionRef.current;
        const nextKeys = new Set(baseKeys);
        const firstIndex = Math.min(startIndex, index);
        const lastIndex = Math.max(startIndex, index);

        for (let rowIndex = firstIndex; rowIndex <= lastIndex; rowIndex += 1) {
            const rowUser = sortedFilteredUsers[rowIndex];
            if (!rowUser) continue;
            const rowKey = getRowKey(rowUser);
            if (mode === 'remove') nextKeys.delete(rowKey);
            else nextKeys.add(rowKey);
        }

        setSelectedKeys(nextKeys);
    }

    function toggleAllUsers() {
        if (sortedFilteredUsers.every((u) => selectedKeys.has(getRowKey(u)))) {
            setSelectedKeys(new Set());
        } else {
            setSelectedKeys(new Set(sortedFilteredUsers.map(getRowKey)));
        }
    }

    function clearSelectedUsers() {
        setSelectedKeys(new Set());
    }

    function handleSort(key) {
        if (sortKey === key) {
            setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
            return;
        }

        setSortKey(key);
        setSortDir('asc');
    }

    function buildPayload() {
        const payload = {
            mode: Number(form.mode),
            sendDate: formatDateTimeForApi(form.sendDate, form.sendTime),
            personName: form.personName,
            studentNo: form.studentNo,
            message: prependMessagePrefix(form.message)
        };

        if (tab === TAB.SMS) {
            return payload;
        }

        payload.subject = form.subject;
        payload.fileCount = 0;
        payload.filePath1 = '';
        payload.filePath2 = '';
        payload.filePath3 = '';
        payload.filePath4 = '';
        payload.filePath5 = '';
        return payload;
    }

    function handleMmsFileChange(event) {
        const list = Array.from(event.target.files || []);
        const imageFiles = list.filter((file) => String(file.type || '').startsWith('image/'));

        if (list.length !== imageFiles.length) {
            alert('이미지 파일만 첨부할 수 있습니다.');
        }

        if (imageFiles.length > MMS_MAX_FILES) {
            alert(`이미지는 최대 ${MMS_MAX_FILES}개까지 첨부할 수 있습니다.`);
        }

        setMmsFiles(imageFiles.slice(0, MMS_MAX_FILES));
    }

    async function uploadMmsImagesToS3(files) {
        const uploadedUrls = [];
        const uploadApiUrl = joinUrl(fnc.svrUrl, '/s3Routes/uploadS3');

        for (let i = 0; i < files.length; i += 1) {
            const file = files[i];
            // MMS 파일 포맷 오류를 피하기 위해 항상 JPG로 변환하여 업로드한다.
            // eslint-disable-next-line no-await-in-loop
            const jpegFile = await convertToJpegFile(file);
            const baseName = sanitizeFileName(String(jpegFile.name || `mms_${i}.jpg`).replace(/\.[^/.]+$/, ''));
            const s3Key = `public/sms/mms/${Date.now()}_${i + 1}_${baseName}.jpg`;

            const formData = new FormData();
            formData.append('file', jpegFile, `${baseName}.jpg`);
            formData.append('s3Key', s3Key);

            // eslint-disable-next-line no-await-in-loop
            const response = await fetch(uploadApiUrl, {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                const text = await response.text();
                throw new Error(text || '이미지 업로드 실패');
            }

            // eslint-disable-next-line no-await-in-loop
            const json = await response.json();
            const key = json?.key || s3Key;

            // MMS 서버 호환을 위해 쿼리스트링 없는 고정 public URL을 전달한다.
            uploadedUrls.push(toS3PublicUrl(key));
        }

        return uploadedUrls;
    }

    async function handleSend() {
        if (!form.message) {
            alert('전송메세지는 필수입니다.');
            return;
        }

        if (Number(form.mode) === 0 && !form.sendDate) {
            alert('예약 모드에서는 전송일을 입력해야 합니다.');
            return;
        }

        if (selectedKeys.size === 0) {
            alert('전송할 회원을 선택해주세요.');
            return;
        }

        const selectedUsers = users
            .filter((item) => selectedKeys.has(getRowKey(item)))
            .sort(compareUsersByName);
        const endpoint = tab === TAB.MMS ? '/smsRoutes/sendMms' : '/smsRoutes/sendSms';
        const previewUser = selectedUsers[0] || {};
        const previewPhone = resolveUserPhone(previewUser) || '번호없음';
        const sendModeText = Number(form.mode) === 0 ? `예약 (${formatDateTimeForApi(form.sendDate, form.sendTime)})` : '즉시';
        const confirmLines = selectedUsers.length === 1
            ? [
                `${previewUser.stName || '선택한 회원'}에게 문자를 전송하시겠습니까?`,
                `번호: ${previewPhone}`,
                `발송방식: ${sendModeText}`
            ]
            : [
                `${selectedUsers.length}명에게 문자를 전송하시겠습니까?`,
                `발송방식: ${sendModeText}`
            ];

        const ok = window.confirm(confirmLines.join('\n'));

        if (!ok) {
            return;
        }

        let successCount = 0;
        let failCount = 0;
        let noPhoneCount = 0;

        let uploadedImageUrls = [];
        if (tab === TAB.MMS && mmsFiles.length > 0) {
            setUploadingImages(true);
            try {
                uploadedImageUrls = await uploadMmsImagesToS3(mmsFiles);
            } catch (error) {
                setUploadingImages(false);
                alert(`MMS 이미지 업로드 실패: ${error.message}`);
                return;
            }
            setUploadingImages(false);
        }

        setSending(true);
        for (const user of selectedUsers) {
            const toPhone = resolveUserPhone(user);
            if (toPhone.length < 10) {
                noPhoneCount += 1;
                continue;
            }

            const payload = buildPayload();
            payload.toPhone = toPhone;
            payload.personName = user.stName || payload.personName;

            if (endpoint === '/smsRoutes/sendMms') {
                payload.subject = payload.subject || '알림';
                payload.fileCount = uploadedImageUrls.length;
                payload.filePath1 = uploadedImageUrls[0] || '';
                payload.filePath2 = uploadedImageUrls[1] || '';
                payload.filePath3 = uploadedImageUrls[2] || '';
                payload.filePath4 = uploadedImageUrls[3] || '';
                payload.filePath5 = uploadedImageUrls[4] || '';
            }

            // 순차 전송으로 서버 부하와 실패 시점 파악을 단순화한다.
            // eslint-disable-next-line no-await-in-loop
            const res = await sendAxiosAsync(endpoint, payload);
            if (res && res.success && Number(res.insertedCount || 0) > 0) {
                successCount += 1;
            } else {
                if (res?.message) {
                    console.error('MMS/SMS send error detail:', res.message, res.detail || '');
                }
                failCount += 1;
            }
        }

        setSending(false);
        const modeText = endpoint === '/smsRoutes/sendMms' ? 'MMS' : 'SMS';
        alert(
            `${modeText} 전송 등록 완료\n성공: ${successCount}건\n실패: ${failCount}건\n전화번호 없음: ${noPhoneCount}건`
        );
    }

    return (
        <div className={`SmsSend ${isMobile ? 'is-mobile' : 'is-desktop'}`}>
            <div className="sms-tab-buttons">
                <button
                    type="button"
                    className="member-log-link"
                    onClick={() => navigate('/admin/login-history')}
                >
                    회원로그
                </button>
                <button
                    type="button"
                    className={tab === TAB.SMS || tab === TAB.MMS ? 'active' : ''}
                    onClick={() => setTab(TAB.SMS)}
                >
                    문자전송
                </button>
                <button
                    type="button"
                    className={tab === TAB.HISTORY ? 'active' : ''}
                    onClick={() => setTab(TAB.HISTORY)}
                >
                    문자내역
                </button>
            </div>

            {tab === TAB.HISTORY ? (
                <SmsSendHistory />
            ) : isMobile ? (
                <SmsSendMobileLayout
                    TAB={TAB}
                    allVisibleSelected={allVisibleSelected}
                    form={form}
                    getRowKey={getRowKey}
                    handleMmsFileChange={handleMmsFileChange}
                    handleSend={handleSend}
                    isSmsOverLimit={isSmsOverLimit}
                    keyword={keyword}
                    loadingUsers={loadingUsers}
                    mmsFiles={mmsFiles}
                    onChange={onChange}
                    onOpenTemplates={() => setTemplateModalOpen(true)}
                    selectedCount={selectedCount}
                    selectedKeys={selectedKeys}
                    sending={sending}
                    setKeyword={setKeyword}
                    setTab={setTab}
                    sortedFilteredUsers={sortedFilteredUsers}
                    tab={tab}
                    tabLabel={tabLabel}
                    toggleAllUsers={toggleAllUsers}
                    toggleUser={toggleUser}
                    uploadingImages={uploadingImages}
                    users={users}
                    fetchUsers={fetchUsers}
                />
            ) : (
                <div className="sms-form-box is-send">
                    <div className="sms-layout">

                        {/* 테이블 패널 (왼쪽) */}
                        <div className="sms-right-panel">
                            <div className="member-toolbar">
                                <div className="member-toolbar-group">
                                    <span className="member-selected-count">선택 {displayedSelectedCount}명</span>
                                    <input
                                        className="member-search"
                                        placeholder="회원 검색"
                                        value={keyword}
                                        onChange={(e) => setKeyword(e.target.value)}
                                    />
                                    <button type="button" onClick={fetchUsers}>새로고침</button>
                                </div>
                                <div className="member-summary">
                                    <span>전체 {users.length}명</span>
                                    <span>검색결과 {sortedFilteredUsers.length}명</span>
                                </div>
                            </div>

                            <div className="member-table-wrap">
                                <table className="member-table">
                                    <thead>
                                        <tr>
                                            <th>{displayedSelectedCount > 0 ? displayedSelectedCount : users.length}</th>
                                            <th>
                                                <input
                                                    type="checkbox"
                                                    checked={allVisibleSelected}
                                                    onChange={toggleAllUsers}
                                                    title="전체 선택"
                                                />
                                            </th>
                                            <th>
                                                <button type="button" className="sort-btn" onClick={() => handleSort('stName')}>
                                                    이름
                                                </button>
                                            </th>
                                            <th>
                                                <button type="button" className="sort-btn" onClick={() => handleSort('id')}>
                                                    아이디
                                                </button>
                                            </th>
                                            <th>
                                                <button type="button" className="sort-btn" onClick={() => handleSort('hp')}>
                                                    휴대폰
                                                </button>
                                            </th>
                                            <th>
                                                <button type="button" className="sort-btn" onClick={() => handleSort('used_amount')}>
                                                    사용금액
                                                </button>
                                            </th>
                                            <th>
                                                <button type="button" className="sort-btn" onClick={() => handleSort('total_charged')}>
                                                    충전금액
                                                </button>
                                            </th>
                                            <th>
                                                <button type="button" className="sort-btn" onClick={() => handleSort('lastLoginAt')}>
                                                    최근로그인
                                                </button>
                                            </th>
                                            <th>
                                                <button type="button" className="sort-btn" onClick={() => handleSort('lastLoginDiff')}>
                                                    diff
                                                </button>
                                            </th>
                                            <th>
                                                <button type="button" className="sort-btn" onClick={() => handleSort('loginCount')}>
                                                    횟수
                                                </button>
                                            </th>
                                            <th>
                                                <button type="button" className="sort-btn" onClick={() => handleSort('sDate')}>
                                                    시작일
                                                </button>
                                            </th>
                                            <th>
                                                <button type="button" className="sort-btn" onClick={() => handleSort('academyName')}>
                                                    학원명
                                                </button>
                                            </th>
                                            <th>
                                                <button type="button" className="sort-btn" onClick={() => handleSort('lastMessage')}>
                                                    최근문자
                                                </button>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loadingUsers ? (
                                            <tr>
                                                <td colSpan="13" className="empty">회원 목록 조회 중...</td>
                                            </tr>
                                        ) : sortedFilteredUsers.length === 0 ? (
                                            <tr>
                                                <td colSpan="13" className="empty">회원이 없습니다.</td>
                                            </tr>
                                        ) : (
                                            sortedFilteredUsers.map((user, index) => (
                                                <tr
                                                    key={`${user.id}-${user.stCode}`}
                                                    className={selectedKeys.has(getRowKey(user)) ? 'is-selected' : ''}
                                                    onMouseDown={(event) => handleUserRowMouseDown(event, user, index)}
                                                    onMouseEnter={(event) => handleUserRowMouseEnter(event, index)}
                                                    onDragStart={(event) => event.preventDefault()}
                                                >
                                                    <td>{index + 1}</td>
                                                    <td
                                                        onMouseDown={(event) => event.stopPropagation()}
                                                        onClick={(event) => { event.stopPropagation(); toggleUser(user); }}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedKeys.has(getRowKey(user))}
                                                            onChange={() => {}}
                                                        />
                                                    </td>
                                                    <td>{user.stName}</td>
                                                    <td>{user.id}</td>
                                                    <td>{user.hp}</td>
                                                    <td className={`td-member-used-amount${Number(user.used_amount) <= 400 ? ' is-low-amount' : ''}`}>
                                                        {formatAmount(user.used_amount)}
                                                    </td>
                                                    <td className="td-member-total-charged">{formatAmount(user.total_charged)}</td>
                                                    <td>{String(user.lastLoginAt || '').slice(0, 10)}</td>
                                                    <td>{Number.isFinite(user.lastLoginDiff) ? user.lastLoginDiff : ''}</td>
                                                    <td>{formatAmount(user.loginCount)}</td>
                                                    <td>{String(user.sDate || '').slice(0, 10)}</td>
                                                    <td className="td-member-academy">{user.academyName}</td>
                                                    <td
                                                        className="td-member-last-message"
                                                        title={user.lastMessage || ''}
                                                    >
                                                        {user.lastMessageAt && (
                                                            <div className={`last-message-date${isTodayMessageDate(user.lastMessageAt) ? ' is-today' : ''}`}>
                                                                {formatMessageDate(user.lastMessageAt)}
                                                            </div>
                                                        )}
                                                        <div className="last-message-content">{messagePreview(user.lastMessage, 36)}</div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>

                        </div>

                        {/* 선택된 문자 수신 대상 패널 (가운데) */}
                        <div className="sms-selected-panel">
                            <div className="selected-panel-header">
                                <h3 className="selected-panel-title">전송 대상 <span>{selectedUsers.length}</span></h3>
                                <button
                                    type="button"
                                    className="selected-clear-btn"
                                    onClick={clearSelectedUsers}
                                    disabled={selectedUsers.length === 0}
                                >
                                    전체삭제
                                </button>
                            </div>

                            <div className="selected-member-grid-wrap">
                                <table className="selected-member-grid">
                                    <thead>
                                        <tr>
                                            <th>이름</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedUsers.length === 0 ? (
                                            <tr>
                                                <td className="selected-empty">선택된 회원이 없습니다.</td>
                                            </tr>
                                        ) : (
                                            selectedUsers.map((user) => (
                                                <tr key={getRowKey(user)}>
                                                    <td>
                                                        <div className="selected-member-cell">
                                                            <span className="selected-member-name" title={user.stName}>{user.stName}</span>
                                                            <button
                                                                type="button"
                                                                className="selected-member-remove"
                                                                onClick={() => toggleUser(user)}
                                                                aria-label={`${user.stName || '회원'} 삭제`}
                                                                title="전송 대상에서 삭제"
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* SMS/MMS 입력 패널 (오른쪽) */}
                        <div className="sms-left-panel sms-send-panel">
                            <div className="sms-send-type-tabs">
                                <button
                                    type="button"
                                    className={`sms-type-tab ${tab === TAB.SMS ? 'active' : ''}`}
                                    onClick={() => setTab(TAB.SMS)}
                                >
                                    SMS
                                </button>
                                <button
                                    type="button"
                                    className={`mms-type-tab ${tab === TAB.MMS ? 'active' : ''}`}
                                    onClick={() => setTab(TAB.MMS)}
                                >
                                    MMS
                                </button>
                                <button
                                    type="button"
                                    className="template-type-tab"
                                    onClick={() => setTemplateModalOpen(true)}
                                >
                                    상용구
                                </button>
                            </div>

                            {tab === TAB.SMS ? (
                                <div className="sms-panel-content">
                                    <div className="sms-inline-schedule-row">
                                        <select className="sms-inline-mode" value={form.mode} onChange={(e) => onChange('mode', e.target.value)}>
                                            <option value={1}>즉시</option>
                                            <option value={0}>예약</option>
                                        </select>
                                        <DatePicker
                                            selected={form.sendDate}
                                            onChange={(date) => onChange('sendDate', date)}
                                            locale={ko}
                                            dateFormat="yyyy-MM-dd"
                                            className="sms-inline-date"
                                            calendarClassName="sms-datepicker-calendar"
                                            popperClassName="sms-datepicker-popper"
                                        />
                                        <input
                                            className="sms-inline-time"
                                            type="time"
                                            value={form.sendTime}
                                            onChange={(e) => onChange('sendTime', e.target.value)}
                                        />
                                    </div>

                                    <label className="message-box no-label-field sms-panel-message">
                                        <textarea
                                            rows={10}
                                            className={isSmsOverLimit ? 'is-over-limit' : ''}
                                            value={form.message}
                                            onChange={(e) => onChange('message', e.target.value)}
                                            placeholder="전송메세지"
                                        />
                                    </label>

                                    <div className="form-actions">
                                        <button className="sms-submit-btn" type="button" onClick={handleSend} disabled={sending || loadingUsers}>
                                            {sending ? '전송 등록 중...' : 'SMS 전송'}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="sms-panel-content">
                                <div className="mms-datetime-row">
                                    <div className="sms-inline-schedule-row">
                                        <select className="sms-inline-mode" value={form.mode} onChange={(e) => onChange('mode', e.target.value)}>
                                            <option value={1}>즉시</option>
                                            <option value={0}>예약</option>
                                        </select>
                                        <DatePicker
                                            selected={form.sendDate}
                                            onChange={(date) => onChange('sendDate', date)}
                                            locale={ko}
                                            dateFormat="yyyy-MM-dd"
                                            className="sms-inline-date"
                                            calendarClassName="sms-datepicker-calendar"
                                            popperClassName="sms-datepicker-popper"
                                        />
                                        <input
                                            className="sms-inline-time"
                                            type="time"
                                            value={form.sendTime}
                                            onChange={(e) => onChange('sendTime', e.target.value)}
                                        />
                                    </div>
                                </div>

                                <label className="label-inline mms-subject-row">
                                    <span>제목</span>
                                    <input value={form.subject} onChange={(e) => onChange('subject', e.target.value)} />
                                </label>

                                <div className="form-grid">
                                    <label>
                                        <span>이미지 첨부 (최대 5개)</span>
                                        <input type="file" accept="image/*" multiple onChange={handleMmsFileChange} />
                                    </label>
                                </div>

                                {mmsFiles.length > 0 && (
                                    <div className="mms-file-list">
                                        {mmsFiles.map((file) => (
                                            <span key={file.name + file.size}>{file.name}</span>
                                        ))}
                                    </div>
                                )}

                                <label className="message-box no-label-field">
                                    <textarea
                                        rows={8}
                                        value={form.message}
                                        onChange={(e) => onChange('message', e.target.value)}
                                        placeholder="전송메세지"
                                    />
                                </label>

                                <div className="form-actions">
                                    <button className="mms-submit-btn" type="button" onClick={handleSend} disabled={sending || loadingUsers || uploadingImages}>
                                        {uploadingImages ? '이미지 업로드 중...' : sending ? '전송 등록 중...' : `${tabLabel} 전송`}
                                    </button>
                                </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <SmsTemplateModal
                open={templateModalOpen}
                onClose={() => setTemplateModalOpen(false)}
                onSelect={(content) => onChange('message', content)}
            />
        </div>
    );
};

export default SmsSend;
